import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

import { catchError, from, Observable, of, timeout } from 'rxjs';

import { environment } from '@env/environment';

/**
 * Tope de espera por el token. El script de reCAPTCHA se carga `async` desde
 * google.com; si no responde, una compra no puede quedar esperando para siempre.
 */
const RECAPTCHA_TIMEOUT_MS = 5000;

declare const grecaptcha: {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
};

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Devuelve el token de reCAPTCHA, o cadena vacía si no se pudo obtener.
   *
   * Nunca falla ni se queda colgado. Si el script no cargó (bloqueadores de
   * publicidad, redes que bloquean google.com, SSR) o Google no responde a
   * tiempo, resuelve con ''. El interceptor manda la request sin token, el
   * backend responde 403 y el checkout lo muestra como "Verificación de
   * seguridad fallida".
   *
   * Es importante que sea así: antes, un `grecaptcha.execute()` rechazado
   * dejaba la promesa sin resolver, el observable no emitía nunca y el botón de
   * pagar quedaba bloqueado sin forma de recuperarse.
   */
  public executeAction(action: string): Observable<string> {
    if (!isPlatformBrowser(this.platformId) || !this.isAvailable()) {
      return of('');
    }

    return from(this.requestToken(action)).pipe(
      timeout(RECAPTCHA_TIMEOUT_MS),
      catchError(() => of('')),
    );
  }

  /** `typeof` sobre un identificador no declarado no lanza, así que es seguro en SSR. */
  private isAvailable(): boolean {
    return typeof grecaptcha !== 'undefined' && typeof grecaptcha.ready === 'function';
  }

  private requestToken(action: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      try {
        grecaptcha.ready(() => {
          grecaptcha.execute(environment.siteKey, { action }).then(resolve, reject);
        });
      } catch (error) {
        // `grecaptcha.ready` puede lanzar de forma síncrona si el script quedó a
        // medias; sin este catch el error escapa del executor de la promesa.
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }
}
